import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, RefreshCw } from "lucide-react";

const TONE = { completed: "text-emerald-200", failed: "text-rose-200", running: "text-amber-200", queued: "text-white/60" };

/** Run history drawer of one scheduled task (polls while a run is active). */
export default function ScheduledTaskRuns({ task, onClose }) {
    const [runs, setRuns] = useState([]);
    const [err, setErr] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/scheduled-tasks/${task.id}/runs?limit=50`);
            setRuns(data.items || []); setErr(null);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [task.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="scheduled-task-runs">
            <div className="w-full max-w-3xl max-h-[88vh] overflow-y-auto bg-[#0c0c0c] border border-white/10 rounded-xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 sticky top-0 bg-[#0c0c0c]">
                    <div>
                        <h2 className="text-base">Runs — {task.name}</h2>
                        <div className="text-[11px] text-white/45">{task.schedule_text} · {task.kind} · {task.client_id}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} className="opacity-60 hover:opacity-100" data-testid="scheduled-task-runs-refresh"><RefreshCw className="w-4 h-4" /></button>
                        <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="scheduled-task-runs-close"><X className="w-4 h-4" /></button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    {err && <div className="text-xs text-rose-200 mb-2">{err}</div>}
                    {runs.length === 0 ? <div className="text-xs text-white/40" data-testid="scheduled-task-runs-empty">No runs yet.</div> : (
                        <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase tracking-wider text-white/50">
                                <tr>
                                    <th className="text-left px-2 py-1.5">Started</th>
                                    <th className="text-left px-2 py-1.5">Trigger</th>
                                    <th className="text-left px-2 py-1.5">Status</th>
                                    <th className="text-left px-2 py-1.5">Duration</th>
                                    <th className="text-left px-2 py-1.5">Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r) => (
                                    <tr key={r.id} className="border-t border-white/5 align-top" data-testid={`scheduled-run-${r.id}`}>
                                        <td className="px-2 py-1.5 font-mono text-white/60">{(r.started_at || r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="px-2 py-1.5">{r.trigger}</td>
                                        <td className={`px-2 py-1.5 ${TONE[r.status] || ""}`} data-testid={`scheduled-run-status-${r.id}`}>{r.status}</td>
                                        <td className="px-2 py-1.5 font-mono text-white/60">{r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                                        <td className="px-2 py-1.5">
                                            {r.flow_run_id && <Link to={`/flows/${r.target_id}`} className="underline text-[#c4b5fd]">flow run {r.flow_run_id.slice(0, 8)}</Link>}
                                            {r.agent_run_id && <Link to={`/agents/${r.target_id}`} className="underline text-[#c4b5fd]">agent run {r.agent_run_id.slice(0, 8)}</Link>}
                                            {r.summary?.flow_status && <span className="text-white/50"> · {r.summary.flow_status}</span>}
                                            {r.summary?.tools?.length > 0 && <span className="text-white/50"> · {r.summary.tools.filter((t) => t.ok).length}/{r.summary.tools.length} tools</span>}
                                            {r.error && <div className="text-rose-200 text-[11px] mt-0.5" data-testid={`scheduled-run-error-${r.id}`}>{r.error}</div>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
